import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Plus, Search, Pencil, Trash2, Building2, Mail, Phone, Archive, ArchiveRestore, FileSpreadsheet } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { api, errMsg } from "@/lib/api";
import { useFetch, useUsers, usePaged } from "@/lib/hooks";
import { useAuth } from "@/context/AuthContext";
import { Pager } from "@/components/core/Pager";
import { ClientAvatar } from "@/components/client/ClientAvatar";
import { ClientImportDialog } from "@/components/client/ClientImportDialog";
import { useSearchParams } from "react-router-dom";
import { ClientDeleteHost } from "@/components/client/ClientDataPanel";
import { PageHeader, Panel, EmptyState } from "@/components/core/AppLayout";
import { ClientFormDialog } from "@/components/core/ClientFormDialog";
import { MobileFilters } from "@/components/core/MobileFilters";
import { ReservedBadge } from "@/components/client/ClientVisibility";
import { ReservedLine } from "@/components/client/ReservedLine";
import { ClientStatoBadge } from "@/components/core/TaskBadges";

const Filter = ({ value, onChange, options, testid, all }) => (
  <Select value={value} onValueChange={onChange}>
    <SelectTrigger className="w-[190px] rounded-xl bg-[#A6DAFC]" data-testid={testid}><SelectValue /></SelectTrigger>
    <SelectContent>
      <SelectItem value="all">{all}</SelectItem>
      {options.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
    </SelectContent>
  </Select>
);

export default function ClientsPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [sp, setSp] = useSearchParams();
  const importOpen = sp.get("importa") === "1";
  const setImport = (o) => setSp(o ? { importa: "1" } : {}, { replace: true });
  const [q, setQ] = useState("");
  const [dq, setDq] = useState("");
  const [stato, setStato] = useState("attivo");
  const [settore, setSettore] = useState("all");
  const [socio, setSocio] = useState("all");
  const [form, setForm] = useState({ open: false, client: null });
  const [del, setDel] = useState(null);
  const { data: users } = useUsers();
  const { data: settori, reload: reloadSettori } = useFetch("/clients/settori");

  useEffect(() => { const t = setTimeout(() => setDq(q), 250); return () => clearTimeout(t); }, [q]);

  const params = { stato: stato === "all" ? "tutti" : stato };
  if (dq) params.q = dq;
  if (settore !== "all") params.settore = settore;
  if (socio !== "all") params.socio_id = socio;
  const paged = usePaged("/clients", params, 25);
  const { items: clients, reload } = paged;

  const refresh = () => { reload(); reloadSettori(); };
  const archive = async (c) => {
    const on = c.stato !== "archiviato";
    try { await api.patch(`/clients/${c.id}/archivio`, { archiviato: on }); toast.success(on ? `«${c.ragione_sociale}» archiviato` : `«${c.ragione_sociale}» ripristinato`); reload(); }
    catch (e) { toast.error(errMsg(e)); }
  };

  return (
    <div data-testid="clients-page">
      <PageHeader eyebrow="Anagrafiche" title="Clienti" subtitle={paged.data ? `${paged.data.total} clienti trovati` : " "}
        actions={<>
          <Button variant="outline" onClick={() => setImport(true)} className="rounded-full h-11 px-5 font-bold border-[#0A1128]/20" data-testid="import-clients-button"><FileSpreadsheet className="h-4 w-4 mr-1.5" />Importa da Excel/CSV</Button>
          <Button onClick={() => setForm({ open: true, client: null })} className="rounded-full bg-[#E8173A] hover:bg-[#C8102E] h-11 px-6 font-bold" data-testid="add-client-button"><Plus className="h-4 w-4 mr-1" />Nuovo cliente</Button>
        </>} />
      <ClientImportDialog open={importOpen} onOpenChange={setImport} onDone={refresh} />

      <div className="flex items-center gap-3 mb-5 core-rise core-delay-1">
        <div className="relative flex-1 min-w-0 max-w-md">
          <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Cerca per nome, referente, email, P.IVA..." className="pl-10 h-10 rounded-xl bg-[#A6DAFC]" data-testid="client-search-input" />
        </div>
        <MobileFilters testid="client-filters">
        <Filter value={stato} onChange={setStato} all="Tutti gli stati" testid="client-filter-stato" options={[{ value: "attivo", label: "Attivi" }, { value: "archiviato", label: "Archiviati" }]} />
        <Filter value={settore} onChange={setSettore} all="Tutti i settori" testid="client-filter-settore" options={(settori || []).map((s) => ({ value: s, label: s }))} />
        <Filter value={socio} onChange={setSocio} all="Tutti i soci" testid="client-filter-socio" options={(users || []).map((u) => ({ value: u.id, label: `${u.nome} ${u.cognome}` }))} />
        </MobileFilters>
      </div>

      <Panel className="core-rise core-delay-2 overflow-hidden">
        {clients && !clients.length ? (
          paged.data?.total === 0 && !dq && stato === "attivo" && settore === "all" && socio === "all" ? (
            <EmptyState icon={Building2} title="Ancora nessun cliente" text="Inizia creando il primo cliente oppure importa in un colpo solo la tua lista da Excel o CSV." testid="clients-empty"
              action={<div className="flex gap-2 justify-center"><Button onClick={() => setForm({ open: true, client: null })} className="rounded-full bg-[#E8173A] hover:bg-[#C8102E] font-bold" data-testid="clients-empty-new"><Plus className="h-4 w-4 mr-1" />Nuovo cliente</Button>
                <Button variant="outline" onClick={() => setImport(true)} className="rounded-full font-bold" data-testid="clients-empty-import"><FileSpreadsheet className="h-4 w-4 mr-1.5" />Importa da Excel/CSV</Button></div>} />
          ) : <EmptyState icon={Building2} title="Nessun cliente trovato" text="Modifica i filtri oppure crea un nuovo cliente." testid="clients-empty" />
        ) : (
          <Table data-testid="clients-table">
            <TableHeader>
              <TableRow className="hover:bg-transparent">
                {["Cliente", "Settore", "Socio responsabile", "Contatti", "Task aperte", "Stato", ""].map((h) => <TableHead key={h} className="text-xs font-bold uppercase tracking-wider">{h}</TableHead>)}
              </TableRow>
            </TableHeader>
            <TableBody>
              {(clients || []).map((c) => (
                <TableRow key={c.id} className="cursor-pointer group" onClick={() => navigate(`/clienti/${c.id}`)} data-testid={`client-row-${c.id}`}>
                  <TableCell>
                    <div className="flex items-center gap-3">
                      <ClientAvatar client={c} testid={`client-avatar-${c.id}`} />
                      <div>
                        <div className="font-bold text-slate-900 group-hover:text-[#E8173A] transition-colors flex items-center gap-2">{c.ragione_sociale}{c.visibilita === "riservato" && <ReservedBadge testid={`client-reserved-${c.id}`} />}</div>
                    {c.visibilita === "riservato" && <ReservedLine clientId={c.id} testid={`client-reserved-line-${c.id}`} />}
                        <div className="text-xs text-slate-500">{c.referente || "—"}</div>
                      </div>
                    </div>
                  </TableCell>
                  <TableCell className="text-sm">{c.settore || "—"}</TableCell>
                  <TableCell className="text-sm">{c.socio_nome || "—"}</TableCell>
                  <TableCell className="text-xs text-slate-600 space-y-0.5">
                    {c.email && <div className="flex items-center gap-1.5"><Mail className="h-3 w-3" />{c.email}</div>}
                    {c.telefono && <div className="flex items-center gap-1.5"><Phone className="h-3 w-3" />{c.telefono}</div>}
                  </TableCell>
                  <TableCell><span className="font-display font-black text-lg text-[#0A1128]">{c.task_aperte}</span></TableCell>
                  <TableCell><ClientStatoBadge stato={c.stato} /></TableCell>
                  <TableCell onClick={(e) => e.stopPropagation()}>
                    <div className="flex gap-1">
                      <button onClick={() => setForm({ open: true, client: c })} className="p-2 rounded-lg hover:bg-[#A6DAFC] text-[#0A1128]" data-testid={`client-edit-${c.id}`}><Pencil className="h-4 w-4" /></button>
                      <button onClick={() => archive(c)} title={c.stato === "archiviato" ? "Ripristina" : "Archivia"} className="p-2 rounded-lg hover:bg-[#A6DAFC] text-[#0A1128]" data-testid={`client-archive-${c.id}`}>
                        {c.stato === "archiviato" ? <ArchiveRestore className="h-4 w-4" /> : <Archive className="h-4 w-4" />}</button>
                      {user.role === "admin" && <button onClick={() => setDel(c)} title="Sposta nel cestino" className="p-2 rounded-lg hover:bg-red-50 text-[#E8173A]" data-testid={`client-delete-${c.id}`}><Trash2 className="h-4 w-4" /></button>}
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
        <Pager data={paged.data} page={paged.page} setPage={paged.setPage} testid="clients-pager" />
      </Panel>

      <ClientFormDialog open={form.open} onOpenChange={(o) => setForm((p) => ({ ...p, open: o }))} client={form.client} users={users || []} settori={settori || []} onSaved={refresh} />
      {del && <ClientDeleteHost client={del} trigger={del.id} onClose={() => { setDel(null); refresh(); }} />}
    </div>
  );
}
